import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';

// ChanakyaX software font family (matches tailwind.config.js theme.fontFamily.sans)
const CX_FONT =
  '"Plus Jakarta Sans", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

/* ── Inline Lucide icons (mail, lock, eye, eye-off, arrow-right, shield-check) ── */
const Icon = ({ name, size = 18, style }) => {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    style,
  };
  switch (name) {
    case 'mail':
      return (
        <svg {...common}>
          <rect width="20" height="16" x="2" y="4" rx="2" />
          <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
        </svg>
      );
    case 'lock':
      return (
        <svg {...common}>
          <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      );
    case 'eye':
      return (
        <svg {...common}>
          <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      );
    case 'eye-off':
      return (
        <svg {...common}>
          <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" />
          <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" />
          <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" />
          <path d="m2 2 20 20" />
        </svg>
      );
    case 'arrow-right':
      return (
        <svg {...common}>
          <path d="M5 12h14" />
          <path d="m12 5 7 7-7 7" />
        </svg>
      );
    case 'shield-check':
      return (
        <svg {...common}>
          <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
          <path d="m9 12 2 2 4-4" />
        </svg>
      );
    default:
      return null;
  }
};

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function PasswordField({ id, label, value, onChange, placeholder, show, onToggle, autoComplete }) {
  return (
    <div className="input-group">
      <label className="input-label" htmlFor={id}>{label}</label>
      <div className="input-wrapper">
        <input
          id={id}
          type={show ? 'text' : 'password'}
          className="glass-input has-right-icon"
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          autoComplete={autoComplete}
          required
        />
        <span className="input-icon"><Icon name="lock" size={18} /></span>
        <button type="button" className="input-action" aria-label="Toggle Password Visibility" onClick={onToggle}>
          <Icon name={show ? 'eye-off' : 'eye'} size={18} />
        </button>
      </div>
    </div>
  );
}

function Login() {
  const location = useLocation();
  // Set by utils/sessionExpiry.js when the app signed the user out because the token ran out.
  const [notice, setNotice] = useState(() => { try { return sessionStorage.getItem('cx-session-notice') || ''; } catch (_) { return ''; } });
  useEffect(() => { try { sessionStorage.removeItem('cx-session-notice'); } catch (_) { /* ignore */ } }, []);
  // mode: 'login' | 'forgot' | 'reset' (from the emailed link) | 'change' (temporary password)
  const [mode, setMode] = useState(location.pathname === '/reset-password' ? 'reset' : 'login');
  const [info, setInfo] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pendingAuth, setPendingAuth] = useState(null);
  const resetToken = new URLSearchParams(location.search).get('token') || '';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [keepSignedIn, setKeepSignedIn] = useState(false);
  const navigate = useNavigate();

  const canvasRef = useRef(null);
  const beaconRef = useRef(null);
  const cardRef = useRef(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setNotice('');
    try {
      const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await response.json();
      if (response.ok) {
        if (data.user?.must_change_password) {
          // Signed in with a temporary password: choose a real one before entering the app.
          setPendingAuth({ token: data.token });
          setNewPassword(''); setConfirmPassword(''); setShowPassword(false);
          setMode('change');
        } else {
          localStorage.setItem('token', data.token);
          localStorage.setItem('user', JSON.stringify(data.user));
          navigate('/dashboard');
        }
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Cannot connect to server. Please try again.');
    }
    setLoading(false);
  };

  const post = async (path, body, token) => {
    const response = await fetch(`${BASE}/api/auth/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    return { ok: response.ok, data };
  };

  const goTo = (next) => {
    setMode(next); setError(''); setInfo('');
    setPassword(''); setNewPassword(''); setConfirmPassword('');
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true); setError(''); setInfo('');
    try {
      const { ok, data } = await post('forgot-password', { email });
      if (ok) setInfo(data.message); else setError(data.message || 'Something went wrong. Please try again.');
    } catch (err) { setError('Cannot connect to server. Please try again.'); }
    setLoading(false);
  };

  const checkNewPassword = () => {
    if (newPassword.length < 8) { setError('Password must be at least 8 characters.'); return false; }
    if (newPassword !== confirmPassword) { setError('The two passwords do not match.'); return false; }
    return true;
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError(''); setInfo('');
    if (!checkNewPassword()) return;
    setLoading(true);
    try {
      const { ok, data } = await post('reset-password', { token: resetToken, password: newPassword });
      if (ok) { setInfo(data.message); setNewPassword(''); setConfirmPassword(''); }
      else setError(data.message || 'Something went wrong. Please try again.');
    } catch (err) { setError('Cannot connect to server. Please try again.'); }
    setLoading(false);
  };

  const handleChange = async (e) => {
    e.preventDefault();
    setError(''); setInfo('');
    if (!checkNewPassword()) return;
    setLoading(true);
    try {
      const { ok, data } = await post('change-password', { current_password: password, new_password: newPassword }, pendingAuth?.token);
      if (ok) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        navigate('/dashboard');
        return;
      }
      setError(data.message || 'Something went wrong. Please try again.');
    } catch (err) { setError('Cannot connect to server. Please try again.'); }
    setLoading(false);
  };

  /* ── 3D tilt & glare on the login card ── */
  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const onMove = (e) => {
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const rotateX = ((y - centerY) / centerY) * -5;
      const rotateY = ((x - centerX) / centerX) * 5;
      card.style.transform = `perspective(1200px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
      card.style.setProperty('--mouse-x', `${x}px`);
      card.style.setProperty('--mouse-y', `${y}px`);
    };
    const onLeave = () => {
      card.style.transform = `perspective(1200px) rotateX(0deg) rotateY(0deg)`;
      card.style.transition = 'transform 0.4s ease-out';
    };
    const onEnter = () => {
      card.style.transition = 'none';
    };

    card.addEventListener('mousemove', onMove);
    card.addEventListener('mouseleave', onLeave);
    card.addEventListener('mouseenter', onEnter);
    return () => {
      card.removeEventListener('mousemove', onMove);
      card.removeEventListener('mouseleave', onLeave);
      card.removeEventListener('mouseenter', onEnter);
    };
  }, []);

  /* ── Constellation lattice background + mouse beacon ── */
  useEffect(() => {
    const canvas = canvasRef.current;
    const beacon = beaconRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    const mouse = { x: -1000, y: -1000, active: false };
    let nodes = [];
    let rafId;

    function initConstellation() {
      nodes = [];
      const density = Math.floor((window.innerWidth * window.innerHeight) / 11000);
      for (let i = 0; i < density; i++) {
        nodes.push({
          x: Math.random() * window.innerWidth,
          y: Math.random() * window.innerHeight,
          vx: (Math.random() - 0.5) * 0.45,
          vy: (Math.random() - 0.5) * 0.45,
          radius: Math.random() * 1.8 + 1,
          isOrange: Math.random() < 0.18,
          pulsePhase: Math.random() * Math.PI * 2
        });
      }
    }

    function resizeCanvas() {
      canvas.width = window.innerWidth * window.devicePixelRatio;
      canvas.height = window.innerHeight * window.devicePixelRatio;
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = window.innerHeight + 'px';
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
      initConstellation();
    }

    function renderConstellation() {
      const connectDist = 135;
      const mouseDist = 170;

      for (let i = 0; i < nodes.length; i++) {
        const p1 = nodes[i];
        p1.x += p1.vx;
        p1.y += p1.vy;

        if (p1.x < 0 || p1.x > window.innerWidth) p1.vx *= -1;
        if (p1.y < 0 || p1.y > window.innerHeight) p1.vy *= -1;

        if (mouse.active) {
          const dx = mouse.x - p1.x;
          const dy = mouse.y - p1.y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < mouseDist) {
            const force = (mouseDist - d) / mouseDist;
            p1.x -= (dx / d) * force * 1.5;
            p1.y -= (dy / d) * force * 1.5;
          }
        }

        for (let j = i + 1; j < nodes.length; j++) {
          const p2 = nodes[j];
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < connectDist) {
            const alpha = (1 - dist / connectDist) * 0.22;
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            if (p1.isOrange || p2.isOrange) {
              ctx.strokeStyle = `rgba(255, 82, 0, ${alpha * 1.4})`;
            } else {
              ctx.strokeStyle = `rgba(147, 197, 253, ${alpha * 0.7})`;
            }
            ctx.lineWidth = 0.9;
            ctx.stroke();
          }
        }

        p1.pulsePhase += 0.02;
        const currentRadius = p1.radius + Math.sin(p1.pulsePhase) * 0.4;

        ctx.beginPath();
        ctx.arc(p1.x, p1.y, currentRadius, 0, Math.PI * 2);
        if (p1.isOrange) {
          ctx.fillStyle = '#ff5200';
          ctx.shadowColor = '#ff5200';
          ctx.shadowBlur = 10;
        } else {
          ctx.fillStyle = 'rgba(255, 255, 255, 0.75)';
          ctx.shadowColor = '#3b82f6';
          ctx.shadowBlur = 4;
        }
        ctx.fill();
        ctx.shadowBlur = 0;
      }
    }

    function animate() {
      ctx.fillStyle = 'rgba(0, 9, 38, 0.22)';
      ctx.fillRect(0, 0, window.innerWidth, window.innerHeight);
      renderConstellation();
      rafId = requestAnimationFrame(animate);
    }

    const onMouseMove = (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
      mouse.active = true;
      if (beacon) {
        beacon.style.left = e.clientX + 'px';
        beacon.style.top = e.clientY + 'px';
        beacon.style.opacity = '1';
      }
    };
    const onMouseLeave = () => {
      mouse.active = false;
      if (beacon) beacon.style.opacity = '0';
    };

    window.addEventListener('resize', resizeCanvas);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseleave', onMouseLeave);

    resizeCanvas();
    animate();

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseleave', onMouseLeave);
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
    };
  }, []);

  return (
    <div className="cx-login-root">
      <style>{`
        .cx-login-root {
          position: fixed;
          inset: 0;
          width: 100%;
          height: 100%;
          overflow: hidden;
          background-color: #000926;
          font-family: ${CX_FONT};
          color: #ffffff;
          -webkit-font-smoothing: antialiased;
          z-index: 0;
        }
        .cx-login-root * { box-sizing: border-box; margin: 0; padding: 0; }

        .cx-login-root #cx-bg-canvas {
          position: absolute;
          top: 0; left: 0;
          width: 100%; height: 100%;
          z-index: 1;
          display: block;
        }

        .cx-login-root .vignette-overlay {
          position: absolute;
          inset: 0;
          pointer-events: none;
          z-index: 2;
          background: radial-gradient(circle at center, transparent 20%, rgba(0, 7, 28, 0.9) 100%);
        }

        .cx-login-root .mouse-beacon {
          position: absolute;
          width: 500px;
          height: 500px;
          border-radius: 50%;
          pointer-events: none;
          z-index: 2;
          background: radial-gradient(circle, rgba(255, 82, 0, 0.08) 0%, rgba(0, 18, 84, 0) 70%);
          transform: translate(-50%, -50%);
          transition: opacity 0.3s ease;
          opacity: 0;
        }

        .cx-login-root .content-wrapper {
          position: relative;
          z-index: 10;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          pointer-events: none;
        }

        .cx-login-root .login-layout {
          display: flex;
          width: 100%;
          max-width: 1100px;
          align-items: center;
          justify-content: space-between;
          gap: 60px;
          pointer-events: auto;
        }

        .cx-login-root .brand-col {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          user-select: none;
        }

        .cx-login-root .brand-svg {
          width: 100%;
          max-width: 420px;
          margin-bottom: 24px;
          filter: drop-shadow(0 10px 20px rgba(0,0,0,0.5));
        }

        .cx-login-root .brand-tagline {
          font-size: 16px;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.6);
          letter-spacing: 0.15em;
          text-transform: uppercase;
          background: linear-gradient(90deg, #ffffff 0%, rgba(255, 255, 255, 0.4) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          padding-left: 4px;
        }

        .cx-login-root .form-col {
          width: 100%;
          max-width: 440px;
          perspective: 1200px;
        }

        .cx-login-root .glass-panel-wrapper {
          animation: cxSlideIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          opacity: 0;
          transform: translateY(30px);
        }

        @keyframes cxSlideIn {
          to { opacity: 1; transform: translateY(0); }
        }

        .cx-login-root .glass-panel {
          position: relative;
          background: rgba(10, 18, 45, 0.65);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-top: 1px solid rgba(255, 255, 255, 0.15);
          border-left: 1px solid rgba(255, 255, 255, 0.15);
          border-radius: 24px;
          padding: 48px 40px;
          box-shadow: 0 30px 60px rgba(0, 0, 0, 0.5), inset 0 0 0 1px rgba(255, 255, 255, 0.02);
          transform-style: preserve-3d;
          transition: transform 0.1s ease-out;
          overflow: hidden;
        }

        .cx-login-root .glass-panel::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          background: radial-gradient(
            circle 250px at var(--mouse-x, 50%) var(--mouse-y, 50%),
            rgba(255, 255, 255, 0.08),
            transparent 80%
          );
          pointer-events: none;
          z-index: 0;
          transition: opacity 0.3s;
          opacity: 0;
        }

        .cx-login-root .glass-panel:hover::before { opacity: 1; }

        .cx-login-root .glass-panel > * {
          position: relative;
          z-index: 1;
          transform: translateZ(20px);
        }

        .cx-login-root .panel-header { margin-bottom: 32px; }

        .cx-login-root .panel-header h2 {
          font-size: 28px;
          font-weight: 800;
          color: #ffffff;
          margin-bottom: 8px;
        }

        .cx-login-root .panel-header p {
          font-size: 13.5px;
          color: rgba(255, 255, 255, 0.6);
          font-weight: 500;
        }

        .cx-login-root .cx-error {
          background: rgba(220, 38, 38, 0.12);
          border: 1px solid rgba(248, 113, 113, 0.4);
          color: #fca5a5;
          padding: 10px 14px;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 500;
          margin-bottom: 20px;
        }

        .cx-login-root .cx-notice {
          background: rgba(245, 158, 11, 0.14);
          border: 1px solid rgba(251, 191, 36, 0.45);
          color: #fcd34d;
          padding: 10px 14px;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 500;
          margin-bottom: 20px;
        }

        .cx-login-root .cx-info {
          background: rgba(22, 163, 74, 0.14);
          border: 1px solid rgba(74, 222, 128, 0.4);
          color: #86efac;
          padding: 10px 14px;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 500;
          margin-bottom: 20px;
        }

        .cx-login-root .link-btn {
          background: none;
          border: 0;
          padding: 0;
          cursor: pointer;
          font-family: inherit;
        }

        .cx-login-root .input-group { margin-bottom: 20px; }

        .cx-login-root .input-label {
          display: block;
          font-size: 13px;
          font-weight: 600;
          color: #ffffff;
          margin-bottom: 8px;
        }

        .cx-login-root .input-wrapper { position: relative; }

        .cx-login-root .input-icon {
          position: absolute;
          left: 16px;
          top: 50%;
          transform: translateY(-50%);
          color: rgba(255, 255, 255, 0.4);
          transition: color 0.3s ease;
          pointer-events: none;
        }

        .cx-login-root .input-action {
          position: absolute;
          right: 16px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: rgba(255, 255, 255, 0.4);
          cursor: pointer;
          display: flex;
          align-items: center;
          transition: color 0.2s ease;
        }

        .cx-login-root .input-action:hover { color: #ffffff; }

        .cx-login-root .glass-input {
          width: 100%;
          background: #0b1120;
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 12px;
          padding: 14px 16px 14px 44px;
          font-family: inherit;
          font-size: 14px;
          color: #ffffff;
          outline: none;
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .cx-login-root .glass-input.has-right-icon { padding-right: 44px; }

        .cx-login-root .glass-input::placeholder { color: rgba(255, 255, 255, 0.25); }

        .cx-login-root .glass-input:hover {
          border-color: rgba(255, 255, 255, 0.18);
          background: #0d1527;
        }

        .cx-login-root .glass-input:focus {
          border-color: rgba(255, 82, 0, 0.6);
          background: #0d1527;
          box-shadow: 0 0 20px rgba(255, 82, 0, 0.1), inset 0 0 0 1px rgba(255, 82, 0, 0.2);
          transform: translateY(-2px);
        }

        .cx-login-root .glass-input:focus + .input-icon { color: #ff5200; }

        .cx-login-root .form-options {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 28px;
          margin-top: 8px;
        }

        .cx-login-root .checkbox-container {
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
        }

        .cx-login-root .checkbox-container input {
          width: 16px;
          height: 16px;
          accent-color: #ffffff;
          cursor: pointer;
        }

        .cx-login-root .checkbox-label {
          font-size: 13px;
          font-weight: 500;
          color: rgba(255, 255, 255, 0.8);
        }

        .cx-login-root .link-text {
          font-size: 13px;
          font-weight: 600;
          color: #3b82f6;
          text-decoration: none;
          transition: color 0.2s;
        }

        .cx-login-root .link-text:hover {
          color: #60a5fa;
          text-decoration: underline;
        }

        .cx-login-root .glass-btn {
          width: 100%;
          padding: 14px;
          background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
          color: #ffffff;
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          font-family: inherit;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all 0.2s ease;
          box-shadow: 0 8px 25px rgba(234, 88, 12, 0.35);
        }

        .cx-login-root .glass-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 30px rgba(234, 88, 12, 0.5);
          background: linear-gradient(135deg, #ff7e27 0%, #f97316 100%);
        }

        .cx-login-root .glass-btn:active {
          transform: translateY(1px);
          box-shadow: 0 4px 15px rgba(234, 88, 12, 0.3);
        }

        .cx-login-root .glass-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
        }

        .cx-login-root .signup-row {
          text-align: center;
          margin-top: 28px;
          font-size: 13px;
          color: rgba(255, 255, 255, 0.6);
        }

        .cx-login-root .security-note {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          margin-top: 24px;
          font-size: 12px;
          font-weight: 500;
          color: rgba(255, 255, 255, 0.4);
        }

        @media (max-width: 960px) {
          .cx-login-root .login-layout {
            flex-direction: column;
            justify-content: center;
            text-align: center;
            gap: 40px;
          }
          .cx-login-root .brand-svg { margin: 0 auto 20px auto; }
          .cx-login-root .brand-tagline { padding-left: 0; }
          .cx-login-root .glass-panel { padding: 40px 32px; }
        }

        @media (max-width: 768px) {
          .cx-login-root .glass-input { font-size: 16px; }
        }
      `}</style>

      {/* Background Canvas */}
      <canvas id="cx-bg-canvas" ref={canvasRef} />

      {/* Ambient Light Vignettes */}
      <div className="vignette-overlay" />
      <div className="mouse-beacon" ref={beaconRef} />

      {/* Transparent Glass Interface */}
      <div className="content-wrapper">
        <div className="login-layout">

          {/* Left Column: Branding */}
          <div className="brand-col">
            <svg className="brand-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15263.4 3560.32" aria-label="ChanakyaX">
              <path fill="#FC5C0B" d="M13821.58 1767.69c29.82,-67.16 129.86,-166.84 176.62,-225.85l1265.19 -1541.84c-72.91,44.24 -958.38,861.93 -1046.05,951.47 -73.2,74.76 -133.38,130.95 -207.85,196.97 -115.46,102.34 -331.11,359.88 -403.04,398.54 -109.82,-138.98 -440.05,-452.63 -508.09,-566.8l-376.95 -0.81c27.87,74.49 129.25,154.75 180.19,212.36l525.64 573.92c-20.18,52.59 -238.08,304.7 -284.77,358.39 -89.85,103.32 -827.29,975.23 -858.4,1042.22l894.31 -685.24c166.19,-124.82 334.53,-406.43 445.38,-488.22 108.99,111.67 205.94,209.69 294.46,317.91 102.93,125.84 97.41,114.34 193.31,113.42 96.42,-0.92 210.43,3.99 304.07,-3.75 -32.27,-73.18 -545.13,-557.76 -594.04,-652.69z"/>
              <path fill="#ffffff" d="M6267.05 2408.83l259.02 7.13 -3.47 -1277.96 -239.85 1.58 -8.4 890.95 -16.86 -19.07c-74.72,-55.17 -653.64,-655.92 -762.6,-768.93 -138.86,-144.03 -123.9,-147.95 -369.32,-128.47l5.46 1302.11c68.73,3.49 191.77,16.85 241.05,-9.12 32.6,-60.92 12.59,-741.84 10.3,-872.68l884.66 874.46z"/>
              <polygon fill="#ffffff" points="1973.31,1919.85 2787.18,1919.55 2789.06,2417.2 3052.41,2415.02 3052.05,1125.45 2788.64,1125.1 2782.22,1675.03 1971.99,1674.38 1969.16,1123.99 1708.58,1129.82 1712.66,2417.23 1970.19,2417.72"/>
              <path fill="#ffffff" d="M1350.08 1370.5c28.93,-41.1 106.82,-185.93 122.92,-246.2 -246.97,4.69 -809.94,-30.26 -1011.86,29.08 -586.87,172.47 -617.64,1004.21 -69,1213.44 207.71,79.21 701.49,56.65 959.06,51.46l115.82 -245.71c-258.8,-0.16 -524.78,4.7 -782.73,-1.19 -226.75,-5.18 -396.19,-136.09 -410.93,-350.47 -39.9,-580.06 603.74,-444.05 1076.71,-450.4z"/>
              <path fill="#ffffff" d="M7553.12 1462.41c83.47,89.42 489.4,901.38 543.34,959.24l303.59 -3.29c-22.67,-76.7 -325.16,-570 -389.75,-689.65 -67.82,-125.63 -128.68,-229.39 -196.06,-348.27 -111.53,-196.8 -123.17,-271.27 -261.57,-267.55 -128.5,3.45 -143.29,92.16 -255.3,288.54 -67.31,118.01 -127.22,221.6 -193.69,336.86 -57.14,99.08 -363.56,612.02 -382.04,678.56l295.5 0.76 535.98 -955.19z"/>
              <path fill="#ffffff" d="M11127.33 2419.38c307.64,0.45 285.66,31 349.1,-91.64 52.53,-101.57 447.59,-816.68 486.36,-857.52 2.39,2.67 5.45,6.42 6.89,8.26l263.55 464.85c88.71,159.81 172.38,318.09 260.87,477.95l311.25 1.16c-31.87,-100.64 -139.79,-252.36 -194.3,-353.77l-391.74 -683.03c-112.09,-189.45 -115.2,-272.65 -256.09,-269.11 -125.13,3.14 -151.35,110.39 -254.62,285.46l-394.12 680.47c-44.51,75.8 -168.52,269.27 -187.15,336.93z"/>
              <path fill="#ffffff" d="M3557.75 2417.64c62.19,-70.09 444.98,-834.11 543.16,-953.52 94.01,159.02 179.06,321.57 269.7,483.62l264.31 472.35 300.47 -3.46c-29.48,-85.74 -136.68,-253.64 -188.52,-341.7l-390.84 -683.98c-103.58,-172.88 -115.74,-274.6 -251.48,-275.12 -108.04,-0.42 -119.9,31.11 -165.28,110.37 -114.63,200.17 -662.8,1132.3 -670.68,1191.01l289.16 0.44z"/>
              <path fill="#ffffff" d="M10842.51 2074.77c-0.04,-97.1 -14.37,-112.55 36.47,-176.79 79.76,-100.78 596.47,-735.04 608.4,-773.2l-311.54 0.68 -463.6 594.6 -461.09 -595.7 -314.62 -1.81 184.17 239.83c66.21,84.11 117,143.75 178.86,225.86 58.35,77.43 123.48,160.78 183.7,235.15 106.16,131.1 93.25,91.15 93.24,307.19l3.43 291.02 261.89 -4.17 0.66 -342.68z"/>
              <path fill="#ffffff" d="M8601.33 2423.64l259.43 -8.84 -0.03 -649c46.99,18.06 178.25,162.93 228.46,209.5l441.53 423.84c62.04,43.21 304.78,15.89 404.37,18.81l-541.56 -509.63c-50.14,-42.1 -142.36,-121.85 -179.64,-168.55 94.49,-110.09 606.98,-577.77 636.47,-624.86 -109.55,-8.78 -302.21,-27.01 -369.2,27.67 -139.93,114.23 -515.97,533.52 -619.31,595.98l-2.18 -626.4 -258.97 -0.43 26.88 1.27c132.99,7 74.23,26.23 236.48,26.57 0,183.27 -20.26,266.99 -3.25,390.82l-24.59 893.23 -234.89 0z"/>
              <path fill="#ffffff" d="M8601.33 2423.64l234.89 0 24.59 -893.23c-17.01,-123.83 3.25,-207.55 3.25,-390.82 -162.25,-0.34 -103.49,-19.57 -236.48,-26.57l-26.88 -1.27 0.63 1311.9z"/>
              <path fill="#F55C0C" d="M11809.25 2410.22c60.65,-79 85.99,-178.99 158.61,-238.89l131.75 243.68 134.65 -1.31c-20.99,-85.95 -95.34,-194.38 -132.9,-267.72 -28.92,-56.48 -96.86,-198.76 -138.57,-237.54 -43.16,54.49 -264.73,429.43 -284.18,507.24l130.64 -5.46z"/>
              <path fill="#FC610C" d="M3811.99 2412.45c147.13,8.7 122.98,19.87 182.24,-85.1 25.23,-44.7 62.35,-112.63 96.31,-154.19 40.97,26.47 128.95,203.5 145.35,242.85l141.64 -6.22 -269.27 -505.88c-47.55,31.35 -274.79,441.19 -296.26,508.53z"/>
              <path fill="rgba(255,255,255,0.25)" d="M8412.9 3457.73c32.75,39.34 7.87,23.04 31.33,30.56 1.29,0.42 51.48,1.56 56.36,1.48 822.51,-13.97 1741.93,-3.33 2568.9,-1.44 103.81,0.24 819.14,19.15 858.72,-17.78 -48.14,-31.25 -3171.64,-15.03 -3515.31,-12.82z"/>
              <path fill="#FC610C" d="M7416.67 2412.85c29.25,-68.43 99.16,-199.87 148.68,-240.16l140.32 242.37 126.98 -3.07c-45.56,-153.06 -203.91,-355.47 -264.24,-507.7l-15.79 15.2c-1.93,2.14 -5.21,5.46 -7.24,7.88 -1.99,2.37 -4.64,5.65 -6.59,8.17 -10.75,13.91 -7.16,10.35 -16.77,25.73l-105.83 186.98c-40.33,70.17 -115.94,181.56 -136.32,265.55l136.81 -0.95z"/>
              <path fill="rgba(255,255,255,0.2)" d="M2862.88 3470.55l2651.36 17.1c70.66,0.07 814.97,23.35 860.36,-17.1 -30.46,-20.25 -3454.69,-31.83 -3511.72,0z"/>
              <path fill="rgba(255,255,255,0.15)" d="M6257.49 2011.46l16.86 19.07 8.4 -890.95 239.85 -1.58 3.47 1277.96 -259.02 -7.13c46.11,33.18 179.62,14.44 262.72,11.41 18.83,-99.84 17.03,-1237.57 -5.39,-1302.04l-246.58 3.59c-34.08,112.49 24.65,864.97 -20.31,889.67z"/>
              <path fill="#F8640A" d="M7312.89 3528.64c117.06,71.38 207.47,-28.14 142.27,-114.42 -44.93,-59.46 -185.39,-40.92 -142.27,114.42z"/>
            </svg>
            <div className="brand-tagline">Automate. Manage. Grow.</div>
          </div>

          {/* Right Column: Form */}
          <div className="form-col">
            <div className="glass-panel-wrapper">
              <div className="glass-panel" ref={cardRef}>

                <div className="panel-header">
                  <h2>{{ login: 'Welcome back', forgot: 'Forgot password?', reset: 'Set a new password', change: 'Choose your password' }[mode]}</h2>
                  <p>{{
                    login: 'Access your intelligent practice workspace.',
                    forgot: "Enter your account email and we'll send you a reset link.",
                    reset: 'Pick a new password for your ChanakyaX account.',
                    change: 'You signed in with a temporary password. Set your own to continue.',
                  }[mode]}</p>
                </div>

                {notice && mode === 'login' && <div className="cx-notice">{notice}</div>}
                {error && <div className="cx-error">{error}</div>}
                {info && <div className="cx-info">{info}</div>}

                {mode === 'forgot' && (
                  <form onSubmit={handleForgot}>
                    <div className="input-group">
                      <label className="input-label" htmlFor="cx-forgot-email">Email Address</label>
                      <div className="input-wrapper">
                        <input
                          id="cx-forgot-email"
                          type="email"
                          className="glass-input"
                          placeholder="Enter your registered email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                        />
                        <span className="input-icon"><Icon name="mail" size={18} /></span>
                      </div>
                    </div>
                    <button type="submit" className="glass-btn" disabled={loading}>
                      {loading ? 'Sending…' : 'Send reset link'}
                      {!loading && <Icon name="arrow-right" size={18} />}
                    </button>
                    <div className="signup-row">
                      <button type="button" className="link-text link-btn" onClick={() => goTo('login')}>&larr; Back to sign in</button>
                    </div>
                  </form>
                )}

                {(mode === 'reset' || mode === 'change') && !(mode === 'reset' && info) && (
                  <form onSubmit={mode === 'reset' ? handleReset : handleChange}>
                    {mode === 'reset' && !resetToken && (
                      <div className="cx-error">This reset link is incomplete. Please use the link from your email, or request a new one.</div>
                    )}
                    <PasswordField id="cx-new-password" label="New password" placeholder="At least 8 characters" autoComplete="new-password"
                      value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                      show={showPassword} onToggle={() => setShowPassword(v => !v)} />
                    <PasswordField id="cx-confirm-password" label="Confirm new password" placeholder="Re-enter the new password" autoComplete="new-password"
                      value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                      show={showPassword} onToggle={() => setShowPassword(v => !v)} />
                    <button type="submit" className="glass-btn" disabled={loading || (mode === 'reset' && !resetToken)}>
                      {loading ? 'Saving…' : mode === 'reset' ? 'Reset password' : 'Save and continue'}
                      {!loading && <Icon name="arrow-right" size={18} />}
                    </button>
                    {mode === 'reset' && (
                      <div className="signup-row">
                        <button type="button" className="link-text link-btn" onClick={() => { navigate('/'); goTo('forgot'); }}>Request a new link</button>
                      </div>
                    )}
                  </form>
                )}

                {mode === 'reset' && info && (
                  <button type="button" className="glass-btn" onClick={() => { navigate('/'); goTo('login'); }}>
                    Go to sign in <Icon name="arrow-right" size={18} />
                  </button>
                )}

                {mode === 'login' && (
                <form onSubmit={handleLogin}>

                  {/* Email */}
                  <div className="input-group">
                    <label className="input-label" htmlFor="cx-email">Email Address</label>
                    <div className="input-wrapper">
                      <input
                        id="cx-email"
                        type="email"
                        className="glass-input"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                      <span className="input-icon"><Icon name="mail" size={18} /></span>
                    </div>
                  </div>

                  {/* Password */}
                  <div className="input-group">
                    <label className="input-label" htmlFor="cx-password">Password</label>
                    <div className="input-wrapper">
                      <input
                        id="cx-password"
                        type={showPassword ? 'text' : 'password'}
                        className="glass-input has-right-icon"
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                      <span className="input-icon"><Icon name="lock" size={18} /></span>
                      <button
                        type="button"
                        className="input-action"
                        aria-label="Toggle Password Visibility"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        <Icon name={showPassword ? 'eye-off' : 'eye'} size={18} />
                      </button>
                    </div>
                  </div>

                  {/* Options */}
                  <div className="form-options">
                    <label className="checkbox-container">
                      <input
                        type="checkbox"
                        checked={keepSignedIn}
                        onChange={(e) => setKeepSignedIn(e.target.checked)}
                      />
                      <span className="checkbox-label">Keep me signed in</span>
                    </label>
                    <button type="button" className="link-text link-btn" onClick={() => goTo('forgot')}>Forgot Password?</button>
                  </div>

                  {/* Submit */}
                  <button type="submit" className="glass-btn" disabled={loading}>
                    {loading ? 'Signing in…' : 'Continue'}
                    {!loading && <Icon name="arrow-right" size={18} />}
                  </button>
                </form>
                )}

                {/* Register Link */}
                {mode === 'login' && (
                  <div className="signup-row">
                    New to ChanakyaX?{' '}
                    <Link to="/register" className="link-text">Start free trial &rarr;</Link>
                  </div>
                )}

                {/* Security Note */}
                <div className="security-note">
                  <Icon name="shield-check" size={14} />
                  Your data is secure with enterprise-grade encryption
                </div>

              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default Login;
